import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Table, Select, Tabs } from '_atoms';
import { TableMobile } from '_atoms/Table/Table';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { PLACEMENTS, SelectSmall } from '_atoms/SelectSmall';
import type { SelectSmallOption } from '_atoms/SelectSmall';
import type { SelectOption as AtomSelectOption } from '_atoms/Select/types';
import type { TableRow } from '_atoms/Table/types';
import type { Opening } from 'store/reducers/jobReducer';
import { SELECT_TYPES } from '_atoms/Select/constants';
import { PopupCloseDeclineOpening } from '_molecules';
import { declineApproveOpening, getSHJobsTableDashboardAPI, getSHServicesTableDashboardAPI } from 'actions';
import { getUser } from 'store/selectors/authorizeSelectors';
import useScreenResize from 'helpers/useScreenResize';
import { ROLES } from 'permission';
import { ReactComponent as JobImage } from 'dist/emptyStates/dashboardOpening.svg';
import { ReactComponent as Vector } from 'dist/icons/Vector1.svg';
import type {
  DashboardSelectOption as SelectOption,
  DashboardTableOptions as TableOptions,
  DashboardGroupingOption as GroupingOption,
  OpeningDeclinePopupState,
} from '_templates/dashboardTypes';
import { MyActiveCandidates } from '../DashboardHRLead/DashboardHRLead';
import { jobTableDataMapper, serviceTableDataMapper, isServiceGrouping, useTabs } from './utils';
import {
  COLUMNS_OPENINGS_SERVICE,
  INITIAL_VALUES_1,
  OPENINGS_GROUP,
  COLUMNS_OPENINGS_SERVICE_TOP,
  COLUMNS_OPENINGS_JOB_TOP,
  INITIAL_VALUES_2,
  COLUMNS_OPENINGS_JOB,
  OPENING_STATUSES_OPTIONS,
} from './constants';
import './style.scss';

interface AllOpeningsServiceProps {
  setTabsLoading: (loading: boolean) => void;
}

function OpeningsTableEmpty() {
  const { t } = useTranslation();

  return (
    <div className="all-openings__empty">
      <JobImage />
      <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {t('translation:dashboard_service_head.text_no_openings')}
      </Typography.Title3>
      <Typography.Text>{t('translation:dashboard_service_head.text_no_openings_hint')}</Typography.Text>
    </div>
  );
}

function AllOpeningsService({ setTabsLoading }: AllOpeningsServiceProps) {
  const tableRef = useRef<HTMLDivElement>(null);
  const tableRequestIdRef = useRef(0);
  const { t } = useTranslation();

  const user = useSelector(getUser)!;
  const showGrouping =
    Array.from(
      new Set(user.roles.filter(r => r.name === ROLES.SERVICE_HEAD && r.service).map(item => item.service!.uuid))
    ).length > 1;

  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<Record<string, unknown>[]>([]);
  const [tableOptions, setTableOptions] = useState<TableOptions>(INITIAL_VALUES_1);
  const [openingStatus, setOpeningStatus] = useState<SelectOption[]>(OPENING_STATUSES_OPTIONS);
  const [grouping, setGrouping] = useState<GroupingOption>(
    showGrouping
      ? ({ ...OPENINGS_GROUP[0], tableData: OPENINGS_GROUP[1].tableData } as GroupingOption)
      : ({ ...OPENINGS_GROUP[1], tableData: OPENINGS_GROUP[0].tableData } as GroupingOption)
  );
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [showOpeningDeclinePopup, setShowOpeningDeclinePopup] = useState<OpeningDeclinePopupState>({
    open: false,
  });

  const onExpand =
    ({ uuid }: Record<string, unknown>) =>
    () => {
      const rowUuid = uuid as string;
      if (!expandedRowKeys.includes(rowUuid)) setExpandedRowKeys(rows => [...rows, rowUuid]);
      else setExpandedRowKeys(rows => rows.filter(r => r !== rowUuid));
    };

  const onClickTitle =
    ({ uuid, name }: Record<string, unknown>) =>
    () => {
      if (isServiceGrouping(grouping.value))
        window.open(`/jobs?services=[{"value":"${uuid}","label":"${name}"}]`, '_blank', 'noopener noreferrer');
      else window.open(`/jobs/${uuid}/show/openings`, '_blank', 'noopener noreferrer');
    };

  function getTableData(group: GroupingOption = grouping, options: TableOptions = tableOptions) {
    tableRequestIdRef.current += 1;
    const requestId = tableRequestIdRef.current;
    setLoading(true);
    setTabsLoading(true);
    if (isServiceGrouping(group.value)) {
      getSHServicesTableDashboardAPI({
        limit: options.limit.value,
        offset: options.offset,
        status: openingStatus.map(({ value }) => value).join(','),
      })
        .then(d => {
          if (requestId !== tableRequestIdRef.current) return;
          setTableOptions(o =>
            group.value !== group.tableData
              ? { ...INITIAL_VALUES_1, itemsCount: d.count }
              : { ...o, itemsCount: d.count }
          );
          const data = serviceTableDataMapper(d.results);
          setTableData(data as Record<string, unknown>[]);
          setGrouping(g => ({ ...g, tableData: g.value }));
          setExpandedRowKeys(data.map(({ uuid }) => uuid as string));
        })
        .catch(() => {
          if (requestId !== tableRequestIdRef.current) return;
          setTableOptions(INITIAL_VALUES_1);
          setTableData([]);
          setGrouping(g => ({ ...g, tableData: g.value }));
        })
        .finally(() => {
          if (requestId !== tableRequestIdRef.current) return;
          setLoading(false);
          setTabsLoading(false);
        });
    } else {
      getSHJobsTableDashboardAPI({
        limit: options.limit.value,
        offset: options.offset,
        status: openingStatus.map(({ value }) => value).join(','),
      })
        .then(d => {
          if (requestId !== tableRequestIdRef.current) return;
          setTableOptions(o =>
            group.value !== group.tableData
              ? { ...INITIAL_VALUES_2, itemsCount: d.count }
              : { ...o, itemsCount: d.count }
          );
          const data = jobTableDataMapper(d.results);
          setTableData(data as Record<string, unknown>[]);
          setGrouping(g => ({ ...g, tableData: g.value }));
          setExpandedRowKeys(data.map(({ uuid }) => uuid as string));
        })
        .catch(() => {
          if (requestId !== tableRequestIdRef.current) return;
          setTableOptions(INITIAL_VALUES_2);
          setTableData([]);
          setGrouping(g => ({ ...g, tableData: g.value }));
        })
        .finally(() => {
          if (requestId !== tableRequestIdRef.current) return;
          setLoading(false);
          setTabsLoading(false);
        });
    }
  }

  const onChangeGrouping = (v: GroupingOption) => {
    setGrouping(v);
    getTableData(v);
  };

  const onChangeFilter = (field: string, item: unknown) => {
    if (field === 'page') {
      const options = {
        ...tableOptions,
        page: item as number,
        offset: ((item as number) - 1) * tableOptions.limit.value,
      };
      setTableOptions(options);
      getTableData(grouping, options);
    } else if (field === 'limit') {
      const options = { ...tableOptions, limit: item as { value: number; label: number }, offset: 0, page: 1 };
      setTableOptions(options);
      getTableData(grouping, options);
    } else {
      const options = { ...tableOptions, [field]: item };
      setTableOptions(options);
      getTableData(grouping, options);
    }
  };

  const onOpeningAction = ({ job_uuid, service_uuid, ...d }: Record<string, unknown>, type: string) => {
    if (type === 'approve') {
      declineApproveOpening(job_uuid as string, service_uuid as string, type, {}).then(() => getTableData());
    } else {
      setShowOpeningDeclinePopup({
        open: true,
        job_uuid: job_uuid as string,
        service_uuid: service_uuid as string,
        opening: d.opening as Opening,
        type,
        job_name: d.job_name as string | undefined,
      });
    }
  };

  const onCloseCloseOpeningPopup = () => setShowOpeningDeclinePopup({ open: false });
  const onDeclineOpening = (reason: string) => {
    if (!showOpeningDeclinePopup.open) return;
    const { job_uuid, service_uuid, type } = showOpeningDeclinePopup;
    declineApproveOpening(job_uuid, service_uuid, type, { reason }).then(() => getTableData());
    setShowOpeningDeclinePopup({ open: false });
  };

  useEffect(getTableData, [openingStatus]);
  useScreenResize('md', tableRef);

  return (
    <>
      <Table
        innerRef={tableRef}
        rowKey="uuid"
        className={cn('all-openings', grouping.tableData)}
        cardTitle={t('translation:dashboard_service_head.title')}
        cardCount={tableOptions.itemsCount}
        emptyComponent={<OpeningsTableEmpty />}
        extra={
          <div className="all-openings__header-extra">
            <Select
              labelInValue
              options={OPENING_STATUSES_OPTIONS as AtomSelectOption[]}
              value={openingStatus}
              onChange={setOpeningStatus}
              mode={SELECT_TYPES.MULTIPLE}
              placeholder={t('translation:dashboard_service_head.ph_status')}
            />
            {showGrouping && (
              <div className="filter-container">
                <SelectSmall
                  label={t('translation:dashboard_service_head.label_group_by')}
                  options={OPENINGS_GROUP}
                  value={grouping}
                  onChange={(option: SelectSmallOption) => onChangeGrouping(option as GroupingOption)}
                  placement={PLACEMENTS.BOTTOM_RIGHT}
                />
              </div>
            )}
          </div>
        }
        showCardFooter={!!tableData.length}
        columns={isServiceGrouping(grouping.tableData) ? COLUMNS_OPENINGS_SERVICE_TOP : COLUMNS_OPENINGS_JOB_TOP}
        loading={loading}
        data={tableData as TableRow[]}
        limit={tableOptions.limit}
        page={tableOptions.page}
        itemsCount={tableOptions.itemsCount}
        updateParams={onChangeFilter}
        expandable={{
          expandedRowKeys,
          // eslint-disable-next-line react/no-unstable-nested-components
          expandedRowRender: ({ openings }: Record<string, unknown>) => (
            <>
              <Table
                rowKey="uuid"
                className={cn('all-openings__sub-table', grouping.tableData)}
                columns={
                  isServiceGrouping(grouping.tableData)
                    ? COLUMNS_OPENINGS_SERVICE(onOpeningAction)
                    : COLUMNS_OPENINGS_JOB(onOpeningAction)
                }
                data={openings as TableRow[]}
                showCardHeader={false}
                showCardFooter={false}
              />
              <TableMobile
                className={cn('all-openings__sub-table all-openings__sub-table-mobile', grouping.tableData)}
                data={openings as TableRow[]}
                columns={
                  isServiceGrouping(grouping.tableData)
                    ? COLUMNS_OPENINGS_SERVICE(onOpeningAction)
                    : COLUMNS_OPENINGS_JOB(onOpeningAction)
                }
              />
            </>
          ),
          // eslint-disable-next-line react/no-unstable-nested-components
          expandIcon: ({ expanded, record }) => (
            <div role="none" className="expanded-row">
              <div className="expanded-row__info">
                <Vector className={cn('expanded-row__icon', { expanded })} onClick={onExpand(record)} />
                <Typography.Text className="name" onClick={onClickTitle(record)}>
                  {record.name}
                  <span>{record.count}</span>
                </Typography.Text>
                {!!record.service && showGrouping && (
                  <Typography.Text className="name__extra">{record.service}</Typography.Text>
                )}
              </div>
              <div className="expanded-row__extra">
                <Typography.Caption>
                  {(t as (key: string, options?: Record<string, unknown>) => string)(
                    'translation:dashboard_service_head.text_total_active_candidates',
                    { count: record.candidates_count as number }
                  )}
                </Typography.Caption>
                <Typography.Caption>
                  {(t as (key: string, options?: Record<string, unknown>) => string)(
                    'translation:dashboard_service_head.text_total_openings',
                    { count: record.openings_count as number }
                  )}
                </Typography.Caption>
              </div>
            </div>
          ),
        }}
      />
      <PopupCloseDeclineOpening
        open={showOpeningDeclinePopup.open}
        opening={showOpeningDeclinePopup.open ? showOpeningDeclinePopup.opening : undefined}
        jobName={showOpeningDeclinePopup.open ? showOpeningDeclinePopup.job_name ?? '' : ''}
        onClose={onCloseCloseOpeningPopup}
        confirm={onDeclineOpening}
      />
    </>
  );
}

export const DashboardServiceHead = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('openings');
  const tabs = useTabs(loading, t);

  return (
    <div className="dashboard-service-head">
      <Tabs className="dashboard-service-head__tabs" activeTab={tab} onChange={setTab} items={tabs} />
      {tab === tabs[0].key && <AllOpeningsService setTabsLoading={setLoading} />}
      {tab === tabs[1].key && <MyActiveCandidates setTabsLoading={setLoading} />}
    </div>
  );
};
